import React, { useState, useEffect, useRef, useCallback } from "react";
import { COLORS } from "../../../theme/colors";
import AgentTile from "./AgentTile";

// agent-experience-redesign-v1.md §4.1 / §4.4 / §11 row 5 — sectioned
// grid with Live / Drafts / Archived. Each section is collapsible;
// Archived is collapsed by default. Within a section tiles wrap to
// fill available columns based on density (96px=auto-fit minmax(112,
// 1fr), 64px=80,1fr).
//
// F18 — chunked rendering for the 200-agent perf concern. Default
// CHUNK_SIZE is 60 tiles per section; an IntersectionObserver on a
// bottom sentinel auto-appends the next chunk when it scrolls into
// view (~one viewport above the end). Cheaper than full
// react-window virtualization and works correctly with our auto-
// wrap grid (grid row count varies with column count which varies
// with viewport width — a row-height-aware virtualizer would be
// overkill for the actual usage curve).

const CHUNK_SIZE = 60;

const SECTIONS = [
  { id: "live",     label: "Live",     match: (a) => (a.status || "live") === "live" || a.status === "active" },
  { id: "drafts",   label: "Drafts",   match: (a) => a.status === "draft" || a.status === "paused" },
  { id: "archived", label: "Archived", match: (a) => a.status === "archived", defaultCollapsed: true },
];

export default function AgentTileGrid({
  agents,
  density = "comfortable",
  onTileClick,
  onTileContextMenu,
  onTileHover,
  onTileHoverEnd,
  emptyState,
}) {
  const [collapsed, setCollapsed] = React.useState(() => {
    const init = {};
    for (const s of SECTIONS) init[s.id] = !!s.defaultCollapsed;
    return init;
  });
  const toggle = (id) => setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));

  const cellMin = density === "compact" ? 80 : 112;

  // Bucket agents into sections; preserve incoming order within a bucket.
  const bucketed = SECTIONS.map((s) => ({ ...s, items: agents.filter(s.match) }));

  // If every section is empty render the parent's empty state. The
  // grid never renders a section with zero items.
  const totalCount = bucketed.reduce((sum, s) => sum + s.items.length, 0);
  if (totalCount === 0) return emptyState || null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {bucketed.map((s) => {
        if (s.items.length === 0) return null;
        const isCollapsed = collapsed[s.id];
        return (
          <section key={s.id}>
            <div
              onClick={() => toggle(s.id)}
              style={{
                display: "flex", alignItems: "center", gap: 8, marginBottom: 10, cursor: "pointer",
                userSelect: "none",
              }}
            >
              <div style={{
                fontSize: 11, fontWeight: 700, color: COLORS.textMuted,
                textTransform: "uppercase", letterSpacing: "0.06em",
              }}>{s.label}</div>
              <div style={{ fontSize: 11, color: COLORS.textDim }}>· {s.items.length}</div>
              <div style={{ flex: 1, height: 1, background: COLORS.border, opacity: 0.5 }} />
              <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: "monospace" }}>
                {isCollapsed ? "▸" : "▾"}
              </div>
            </div>
            {!isCollapsed && (
              <ChunkedGrid
                items={s.items}
                cellMin={cellMin}
                density={density}
                onTileClick={onTileClick}
                onTileContextMenu={onTileContextMenu}
                onTileHover={onTileHover}
                onTileHoverEnd={onTileHoverEnd}
              />
            )}
          </section>
        );
      })}
    </div>
  );
}

// F18 — Chunked grid renderer. Renders the first CHUNK_SIZE items
// immediately; an IntersectionObserver on a bottom sentinel pulls
// the next chunk into view as the user approaches the end.
// Resets to one chunk when `items` changes (search filter,
// section reorder, etc.) so the visible-count never grows
// unboundedly through filter churn.
function ChunkedGrid({ items, cellMin, density, onTileClick, onTileContextMenu, onTileHover, onTileHoverEnd }) {
  const [visibleCount, setVisibleCount] = useState(() => Math.min(items.length, CHUNK_SIZE));
  const sentinelRef = useRef(null);

  // Reset on items array identity change (filter / sort applied
  // upstream creates a new array). Reset is cheap — it just clamps
  // visibleCount back to one chunk so subsequent observer pulls
  // re-grow as needed.
  useEffect(() => {
    setVisibleCount(Math.min(items.length, CHUNK_SIZE));
  }, [items]);

  // Auto-grow when sentinel is near the viewport.
  useEffect(() => {
    if (visibleCount >= items.length) return;       // nothing to load
    if (typeof IntersectionObserver === "undefined") {
      // Fallback: render everything (older browsers / SSR).
      setVisibleCount(items.length);
      return;
    }
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const obs = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setVisibleCount(prev => Math.min(items.length, prev + CHUNK_SIZE));
        }
      }
    }, { rootMargin: "400px" });   // pull next chunk one viewport before reaching the bottom
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [visibleCount, items.length]);

  const visibleItems = visibleCount >= items.length ? items : items.slice(0, visibleCount);
  const hasMore = visibleCount < items.length;

  return (
    <>
      <div style={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(${cellMin}px, 1fr))`,
        gap: density === "compact" ? 6 : 10,
        alignItems: "start",
      }}>
        {visibleItems.map((agent) => (
          <AgentTile
            key={agent.id}
            agent={agent}
            density={density}
            onClick={onTileClick}
            onContextMenu={onTileContextMenu}
            onHover={onTileHover}
            onHoverEnd={onTileHoverEnd}
          />
        ))}
      </div>
      {hasMore && (
        <div ref={sentinelRef} style={{
          padding: "12px 0", textAlign: "center", color: COLORS.textDim, fontSize: 11,
        }}>
          {`Showing ${visibleCount} of ${items.length} — scrolling reveals more`}
        </div>
      )}
    </>
  );
}
